"use client";

import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Home, Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

interface ErrorElementProps {
  statusCode?: number;
  title?: string;
  description?: string;
}

const ErrorElement = ({ statusCode = 404, title, description }: ErrorElementProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(["common"]);

  const defaultTitle =
    statusCode === 404 ? t("common:errors.notFoundTitle") : t("common:errors.genericErrorTitle");

  const defaultDescription =
    statusCode === 404
      ? t("common:errors.notFoundDescription")
      : t("common:errors.genericErrorDescription");

  return (
    <div className="bg-background flex min-h-[calc(100dvh-4rem)] w-full items-center justify-center p-4">
      <Card className="bg-foreground/50 relative mx-auto w-full max-w-xl overflow-hidden rounded-lg border-none p-20 backdrop-blur-xl">
        <div className="absolute inset-0 opacity-35">
          <div className="absolute -top-40 -right-40 size-80 rounded-full bg-gradient-to-br from-red-600 to-red-800" />
          <div className="absolute -bottom-30 -left-30 size-60 rounded-full bg-gradient-to-tr from-red-500 to-red-600" />
        </div>
        <CardContent className="relative z-50 flex flex-col items-center p-8 text-center">
          <motion.div
            animate={{ scale: 1, opacity: 1 }}
            className="bg-primary/10 mb-6 flex h-24 w-24 items-center justify-center rounded-full"
            initial={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.5, type: "spring" }}
          >
            <Search className="text-muted-foreground h-12 w-12" />
          </motion.div>

          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="space-y-2">
              <h1 className="text-accent-foreground text-3xl font-bold">{title || defaultTitle}</h1>
              <p className="text-muted-foreground">
                <span className="font-mono">{statusCode} - </span>
                {description || defaultDescription}
              </p>
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <Button
                className="flex items-center gap-2"
                variant="outline"
                onClick={() => navigate(-1)}
              >
                <ArrowLeft className="h-4 w-4" />
                {t("common:goBack")}
              </Button>

              <Button className="flex items-center gap-2" onClick={() => navigate("/")}>
                <Home className="h-4 w-4" />
                {t("common:goHome")}
              </Button>
            </div>
          </motion.div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ErrorElement;
